'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { EGalleryDimension } from '@/types/product'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import 'photoswipe/dist/photoswipe.css'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useProductDetailSafe } from '../context/product-detail-context'
import ProductGalleryNavigation from './product-gallery-navigation'
import ProductVideo from './product-video'
import ProductVideoThumbnail from './product-video-thumbnail'

type Props = {
  className?: string
}

export const ProductGallery: React.FC<Props> = ({ className }) => {
  const { colorOptionActive, product } = useProductDetailSafe()

  const hasVideo = !!product?.youtube_video
  const productImage =
    colorOptionActive?.image?.slice(0, -1) ||
    product?.images?.slice(0, -1) ||
    []

  const videoRef = useRef<HTMLVideoElement>(null)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)

      if (!hasVideo || !videoRef.current) return

      if (index === 0) {
        videoRef.current.muted = true
        videoRef.current
          .play()
          .then(() => setIsVideoPlaying(true))
          .catch((e) => console.error('Error playing video:', e))
      } else if (isVideoPlaying && !videoRef.current.paused) {
        videoRef.current.pause()
        setIsVideoPlaying(false)
      }
    },
    [emblaApi, hasVideo, isVideoPlaying]
  )

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const newIndex = emblaApi.selectedScrollSnap()
    setSelectedIndex(newIndex)
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())

    if (!hasVideo || !videoRef.current) return

    if (newIndex === 0) {
      videoRef.current.muted = true
      videoRef.current
        .play()
        .then(() => setIsVideoPlaying(true))
        .catch((e) => console.error('Error playing video:', e))
    } else if (!videoRef.current.paused) {
      setIsVideoPlaying(false)
      videoRef.current.pause()
    }
  }, [emblaApi, hasVideo])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    let lightbox: PhotoSwipeLightbox | null = new PhotoSwipeLightbox({
      gallery: '#' + 'product-gallery',
      children: 'a',
      pswpModule: () => import('photoswipe'),
    })

    if (product?.youtube_video) {
      lightbox.on('uiRegister', () => {
        lightbox?.pswp?.ui?.registerElement({
          name: 'custom-video-support',
          order: 9,
          isButton: false,
          appendTo: 'root',
          onInit: (element: HTMLElement, pswp) => {
            let videoEl: HTMLVideoElement | null = null

            pswp.on('change', () => {
              const currSlide = pswp.currSlide
              if (
                currSlide &&
                currSlide.data.element?.dataset.pswpType === 'video'
              ) {
                const videoSrc = currSlide.data.element?.dataset.pswpVideoSrc
                if (videoSrc) {
                  if (videoEl) {
                    videoEl.pause()
                    videoEl.remove()
                    videoEl = null
                  }

                  videoEl = document.createElement('video')

                  const contentEl =
                    currSlide.container.querySelector('.pswp__content')
                  if (contentEl) {
                    contentEl.innerHTML = ''
                    contentEl.appendChild(videoEl)
                  }
                }
              } else if (videoEl) {
                videoEl.pause()
                videoEl = null
              }
            })

            pswp.on('destroy', () => {
              if (videoEl) {
                videoEl.pause()
                videoEl = null
              }
            })
          },
        })
      })
    }

    lightbox.init()

    return () => {
      if (lightbox) {
        lightbox.destroy()
        lightbox = null
      }
    }
  }, [product?.youtube_video])

  useEffect(() => {
    if (!hasVideo || !videoRef.current) return

    videoRef.current.muted = true

    const handleVideoClick = () => {
      if (!videoRef.current) return

      if (videoRef.current.paused) {
        videoRef.current.muted = true
        videoRef.current
          .play()
          .then(() => setIsVideoPlaying(true))
          .catch((e) => console.error('Error playing video:', e))
      } else {
        setIsVideoPlaying(false)
        videoRef.current.pause()
      }
    }

    const videoElement = videoRef.current
    videoElement.addEventListener('click', handleVideoClick)

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('click', handleVideoClick)
      }
    }
  }, [hasVideo])

  const handleVideoThumbnailClick = useCallback(() => {
    if (!videoRef.current) return

    if (selectedIndex !== 0) {
      scrollTo(0)
      return
    }

    try {
      if (videoRef.current.paused) {
        videoRef.current.muted = true
        videoRef.current
          .play()
          .then(() => {
            setIsVideoPlaying(true)
          })
          .catch((e) => console.error('Error playing video:', e))
      } else {
        setIsVideoPlaying(false)
        videoRef.current.pause()
      }
    } catch (error) {
      console.error('Error toggling video playback:', error)
    }
  }, [selectedIndex, scrollTo])

  console.log('productImage', productImage)
  return (
    <section
      className={cn(
        'relative lg:sticky lg:top-24 lg:flex lg:h-fit lg:gap-2',
        className
      )}
    >
      <div className='no-scrollbar absolute left-5 top-6 z-[1] flex max-h-[calc(100vh-400px)] flex-col gap-2 overflow-y-auto lg:static lg:gap-1.5'>
        {hasVideo && (
          <ProductVideoThumbnail
            posterSrc={
              productImage[0]?.src
                ? ImageHelper.getImageUrl(productImage[0].src, '270_306')
                : ''
            }
            isPlaying={isVideoPlaying}
            isActive={selectedIndex === 0}
            onClick={handleVideoThumbnailClick}
          />
        )}
        {productImage?.map((item, index) => (
          <button
            key={index}
            className={cn(
              'h-10 min-h-10 w-10 min-w-10 overflow-hidden rounded border border-neutral-300 lg:h-14.5 lg:w-11 lg:border-0',
              selectedIndex !== (hasVideo ? index + 1 : index) && 'opacity-50'
            )}
            onClick={() => scrollTo(hasVideo ? index + 1 : index)}
          >
            <Image
              src={ImageHelper.getImageUrl(item.src, '270_306')}
              alt={`Product side ${item.src}`}
              width={30}
              height={40}
              className='h-auto w-full object-cover'
              unoptimized
            />
          </button>
        ))}
      </div>

      <div
        className='relative h-full w-full overflow-hidden md:rounded-lg'
        ref={emblaRef}
      >
        <div className='pswp-gallery flex gap-2' id={'product-gallery'}>
          {hasVideo && (
            <div
              key={'product-video'}
              className='relative block flex-[0_0_100%]'
            >
              <ProductVideo
                videoSrc={product?.youtube_video || ''}
                videoRef={videoRef}
                onPlayingChange={setIsVideoPlaying}
              />
            </div>
          )}
          {productImage?.map((item, index) => (
            <a
              href={ImageHelper.getImageUrl(item.src)}
              data-pswp-width={EGalleryDimension.WIDTH}
              data-pswp-height={EGalleryDimension.HEIGHT}
              key={'reviews-gallery' + '-' + index}
              target='_blank'
              rel='noopener noreferrer'
              className='relative block flex-[0_0_100%] cursor-zoom-in'
            >
              <Image
                src={ImageHelper.getImageUrl(item.src, 'xl', 713, 1050, 95)}
                alt={`Product Image ${item.src}`}
                width={713}
                height={1050}
                className='h-full w-full object-cover'
                fetchPriority={!hasVideo && index === 0 ? 'high' : 'low'}
                sizes='(max-width: 768px) 100vw, 375px'
                unoptimized={!hasVideo && index === 0}
                priority={!hasVideo && index === 0}
              />
            </a>
          ))}
        </div>
        {!product?.icon_thumbnail?.hide && product?.icon_thumbnail?.image && (
          <div className='absolute bottom-0 left-0 w-full'>
            <Image
              src={ImageHelper.getImageUrl(product.icon_thumbnail.image)}
              alt='Product footer Image'
              width={375}
              height={54}
              className='w-full object-contain'
            />
          </div>
        )}
      </div>

      {product?.gender_type === 'FEMALE' && (
        <Image
          src='/images/logo-active-for-women.svg'
          width={61}
          height={20}
          className='absolute bottom-1 right-5.5 top-6'
          alt='Active for women logo'
        />
      )}
      {(canScrollPrev || canScrollNext) && (
        <ProductGalleryNavigation
          scrollPrev={scrollPrev}
          canScrollPrev={canScrollPrev}
          canScrollNext={canScrollNext}
          scrollNext={scrollNext}
          className={
            !product?.icon_thumbnail?.hide && product?.icon_thumbnail?.image
              ? 'bottom-[4.5rem]'
              : 'bottom-4'
          }
        />
      )}
    </section>
  )
}
